import { create } from 'zustand';
import api from '@/services/api';
import type { GenerationSessionSummary, GenerationSessionDetail, SaveSessionRequest, SaveSessionResponse } from '@/types';

interface HistoryState {
  sessions: GenerationSessionSummary[];
  isLoading: boolean;
  hasMore: boolean;
  total: number;
  page: number;
  search: string;
  selectedSessionId: string | null;
  selectedSession: GenerationSessionDetail | null;
  isLoadingSession: boolean;

  fetchSessions: (workspaceId: string, reset?: boolean) => Promise<void>;
  loadMore: (workspaceId: string) => Promise<void>;
  searchSessions: (workspaceId: string, search: string) => Promise<void>;
  fetchSession: (workspaceId: string, sessionId: string) => Promise<GenerationSessionDetail | null>;
  saveGeneration: (workspaceId: string, data: SaveSessionRequest) => Promise<SaveSessionResponse | null>;
  deleteSession: (workspaceId: string, sessionId: string) => Promise<void>;
  renameSession: (workspaceId: string, sessionId: string, title: string) => Promise<void>;
  clearSelection: () => void;
  reset: () => void;
}

export const useHistoryStore = create<HistoryState>((set, get) => ({
  sessions: [],
  isLoading: false,
  hasMore: false,
  total: 0,
  page: 1,
  search: '',
  selectedSessionId: null,
  selectedSession: null,
  isLoadingSession: false,

  fetchSessions: async (workspaceId, reset = false) => {
    if (reset) {
      set({ sessions: [], page: 1, hasMore: false, total: 0, search: '' });
    }
    set({ isLoading: true });
    try {
      const response = await api.getGenerationSessions(workspaceId, 1, 20, get().search || undefined);
      set({
        sessions: response.sessions,
        hasMore: response.has_more,
        total: response.total,
        page: 1,
        isLoading: false,
      });
    } catch (err: any) {
      console.error('Failed to fetch history sessions:', err);
      set({ isLoading: false });
    }
  },

  loadMore: async (workspaceId) => {
    const { page, hasMore, isLoading, search } = get();
    if (!hasMore || isLoading) return;
    const nextPage = page + 1;
    set({ isLoading: true });
    try {
      const response = await api.getGenerationSessions(workspaceId, nextPage, 20, search || undefined);
      set((state) => ({
        sessions: [...state.sessions, ...response.sessions],
        hasMore: response.has_more,
        total: response.total,
        page: nextPage,
        isLoading: false,
      }));
    } catch (err: any) {
      console.error('Failed to load more sessions:', err);
      set({ isLoading: false });
    }
  },

  searchSessions: async (workspaceId, search) => {
    set({ search, isLoading: true, sessions: [], page: 1 });
    try {
      const response = await api.getGenerationSessions(workspaceId, 1, 20, search || undefined);
      set({
        sessions: response.sessions,
        hasMore: response.has_more,
        total: response.total,
        page: 1,
        isLoading: false,
      });
    } catch (err: any) {
      console.error('Failed to search sessions:', err);
      set({ isLoading: false });
    }
  },

  fetchSession: async (workspaceId, sessionId) => {
    set({ isLoadingSession: true, selectedSessionId: sessionId });
    try {
      const session = await api.getGenerationSession(workspaceId, sessionId);
      set({ selectedSession: session, isLoadingSession: false });
      return session;
    } catch (err: any) {
      console.error('Failed to fetch session:', err);
      set({ isLoadingSession: false, selectedSessionId: null });
      return null;
    }
  },

  saveGeneration: async (workspaceId, data) => {
    try {
      const response = await api.saveGenerationSession(workspaceId, data);
      // Refresh session list after save
      get().fetchSessions(workspaceId);
      return response;
    } catch (err: any) {
      console.error('Failed to save generation to history:', err);
      return null;
    }
  },

  deleteSession: async (workspaceId, sessionId) => {
    try {
      await api.deleteGenerationSession(workspaceId, sessionId);
      set((state) => ({
        sessions: state.sessions.filter((s) => s.id !== sessionId),
        total: state.total - 1,
        selectedSessionId: state.selectedSessionId === sessionId ? null : state.selectedSessionId,
        selectedSession: state.selectedSessionId === sessionId ? null : state.selectedSession,
      }));
    } catch (err: any) {
      console.error('Failed to delete session:', err);
    }
  },

  renameSession: async (workspaceId, sessionId, title) => {
    try {
      const updated = await api.renameGenerationSession(workspaceId, sessionId, title);
      set((state) => ({
        sessions: state.sessions.map((s) =>
          s.id === sessionId ? { ...s, title: updated.title } : s
        ),
      }));
    } catch (err: any) {
      console.error('Failed to rename session:', err);
    }
  },

  clearSelection: () => {
    set({ selectedSessionId: null, selectedSession: null });
  },

  reset: () => {
    set({
      sessions: [],
      isLoading: false,
      hasMore: false,
      total: 0,
      page: 1,
      search: '',
      selectedSessionId: null,
      selectedSession: null,
      isLoadingSession: false,
    });
  },
}));
